import styles from "./SolarProductConfirmation.module.scss";
import React, { memo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AppDispatch } from "../../../../stores/store";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { SolarRecommendationForm } from "../../../../model/solar-recommendation-form/solar-recommendation-form";
import { ApplicationRoutes } from "../../../../routes";
import { hideLoader, showLoader } from "../../../../stores/slices/loader-slice";
import { addInstallationAddress, addProductToCart } from "../../../../stores/slices/buy-solar-product-slice";
import { useLazyGetCustomerInfoQuery } from "../../../../service/customer-service";
import { initialize } from "../../../../stores/slices/customer-registration-slice";
import { useCreateAnOrderMutation } from "../../../../service/order-service";
import { AvailableOffer, SolarProductObject } from "../../../../model/solar-product/solar-product-with-recommendations";

type Props = {};

type SelectedSolarProduct = {
	solarProduct: SolarProductObject;
	selectedPaymentPlan: AvailableOffer;
};

const SolarProductConfirmation: React.FC<Props> = memo((props: Props) => {
	const dispatch = useDispatch<AppDispatch>();

	const location = useLocation();

	const navigator = useNavigate();

	const [getCustomerInfo, _] = useLazyGetCustomerInfoQuery();

	const [createAnOrder, __] = useCreateAnOrderMutation();

	const selectedItem: SelectedSolarProduct = location.state;

	const currentDate = new Date();

	const getDayWithSuffix = () => {
		const day = currentDate.getDate();

		let suffix = "th";

		if (day % 10 === 1 && day % 100 !== 11) {
			suffix = "st";
		} else if (day % 10 === 2 && day % 100 !== 12) {
			suffix = "nd";
		} else if (day % 10 === 3 && day % 100 !== 13) {
			suffix = "rd";
		}

		return `${day}${suffix}`;
	};

	const getMaturityDate = () => {
		const options: Intl.DateTimeFormatOptions = { year: "numeric", month: "short", day: "numeric" };
		return `${new Date(currentDate.setMonth(currentDate.getMonth() + selectedItem.selectedPaymentPlan.months)).toLocaleDateString("en-US", options)}`;
	};

	const onConfirm = async () => {
		const formFromStorage = sessionStorage.getItem("solar-recommendation-form");

		const parsedForm: SolarRecommendationForm | undefined = formFromStorage ? JSON.parse(formFromStorage) : undefined;

		dispatch(
			addProductToCart({
				productDetailsId: selectedItem.solarProduct.solarProduct.id,
				durationOfPaymentsInMonths: selectedItem.selectedPaymentPlan.months,
				roofType: parsedForm?.installation?.roofType?.id,
				numberOfFloors: parsedForm?.installation?.floors,
			})
		);

		try {
			dispatch(showLoader());

			const result = await getCustomerInfo(undefined, true).unwrap();

			dispatch(hideLoader());

			if (result.monoVerified && !!result.address && !!result.companyName && !!result.idCardNumber) {
				try {
					await createAnOrder({
						productId: selectedItem.solarProduct.solarProduct.id!,
						durationOfPaymentsInMonths: selectedItem.selectedPaymentPlan.months,
						roofType: parsedForm?.installation?.roofType?.id!,
						numberOfFloors: parsedForm?.installation?.floors!,
					}).unwrap();

					toast.success("Order created successfully");
					navigator(`/${ApplicationRoutes.CUSTOMER_BASE}/${ApplicationRoutes.DASHBOARD}`);
					return;
				} catch (error: any) {
					toast.error(error.data?.title || "Something went wrong. Please try again.");
					return;
				}
			}

			dispatch(addInstallationAddress(result.address ?? ""));

			dispatch(initialize(result));
			navigator(`${ApplicationRoutes.CUSTOMER_SIGNUP}`, { state: { orderUrl: location.pathname } });
		} catch (error: any) {
			dispatch(hideLoader());
			toast.error(error.data?.title || "Failed to sign up. Please try again.");
		}
	};

	return (
		<>
			{
				<div className={styles.container}>
					<div className={styles.title}>Confirm Your Solar Power Payment Breakdown</div>
					<div className={styles.centerSection}>
						<div className={styles.centerSectionTitle}>Basic Information</div>
						<div className={styles.row}>
							<div className={styles.property}>Loan Tenure</div>
							<div className={styles.value} style={{ textDecoration: "underline" }}>
								{selectedItem.selectedPaymentPlan?.months} Month(s)
							</div>
						</div>
						<div className={styles.row}>
							<div className={styles.property}>Maturity Date</div>
							<div className={styles.value}>{getMaturityDate()}</div>
						</div>
						<div className={styles.row}>
							<div className={styles.property}>Repayment Date</div>
							<div className={styles.value}>Monthly on {getDayWithSuffix()}</div>
						</div>
						<div className={styles.row}>
							<div className={styles.property}>Monthly Repayment Amount</div>
							<div className={styles.value}>₦{selectedItem.selectedPaymentPlan.price.toLocaleString()}</div>
						</div>
						<div className={styles.row}>
							<div className={styles.property}>Initial Deposit</div>
							<div className={styles.value}>₦{(selectedItem.solarProduct.solarProduct.price * 0.3).toLocaleString()}</div>
						</div>
						<div className={styles.row}>
							<div className={styles.property}>Least amount payable</div>
							<div className={styles.value}>₦{selectedItem.solarProduct.availableOffers.sort((a, b) => a.price - b.price)[0].price.toLocaleString()}</div>
						</div>
						<div className={styles.row}>
							<div className={styles.property}>Total amount payable</div>
							<div className={styles.value}>
								₦
								{(
									selectedItem.solarProduct.solarProduct.price * 0.3 +
									selectedItem.selectedPaymentPlan.price * selectedItem.selectedPaymentPlan.months
								).toLocaleString()}
							</div>
						</div>
					</div>
					<div className={styles.buttonSection}>
						<button className={styles.leftBtn} onClick={() => navigator(-1)}>
							<svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
								<mask id="mask0_1116_10130" maskUnits="userSpaceOnUse" x="0" y="0" width="25" height="24">
									<rect width="24" height="24" transform="matrix(-1 0 0 1 24.5 0)" fill="#D9D9D9" />
								</mask>
								<g mask="url(#mask0_1116_10130)">
									<path
										d="M11.5538 12L15.6269 7.92689C15.7654 7.78844 15.8362 7.6144 15.8394 7.40479C15.8426 7.19519 15.7718 7.01795 15.6269 6.87309C15.482 6.7282 15.3064 6.65576 15.1 6.65576C14.8936 6.65576 14.718 6.7282 14.5731 6.87309L10.0789 11.3673C9.98529 11.4609 9.91926 11.5596 9.88081 11.6635C9.84235 11.7673 9.82311 11.8795 9.82311 12C9.82311 12.1205 9.84235 12.2327 9.88081 12.3365C9.91926 12.4404 9.98529 12.5391 10.0789 12.6327L14.5731 17.1269C14.7116 17.2654 14.8856 17.3362 15.0952 17.3394C15.3048 17.3426 15.482 17.2718 15.6269 17.1269C15.7718 16.982 15.8442 16.8064 15.8442 16.6C15.8442 16.3936 15.7718 16.218 15.6269 16.0731L11.5538 12Z"
										fill="#DE841B"
									/>
								</g>
							</svg>
							Back to editing
						</button>
						<button className={styles.rightBtn} onClick={onConfirm}>
							Confirm
							<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
								<mask id="mask0_1116_10138" maskUnits="userSpaceOnUse" x="0" y="0" width="24" height="24">
									<rect width="24" height="24" fill="#D9D9D9" />
								</mask>
								<g mask="url(#mask0_1116_10138)">
									<path
										d="M12.9462 12L8.87309 7.92689C8.73462 7.78844 8.66379 7.6144 8.66059 7.40479C8.65737 7.19519 8.7282 7.01795 8.87309 6.87309C9.01795 6.7282 9.19359 6.65576 9.39999 6.65576C9.60639 6.65576 9.78202 6.7282 9.92689 6.87309L14.4211 11.3673C14.5147 11.4609 14.5807 11.5596 14.6192 11.6635C14.6577 11.7673 14.6769 11.8795 14.6769 12C14.6769 12.1205 14.6577 12.2327 14.6192 12.3365C14.5807 12.4404 14.5147 12.5391 14.4211 12.6327L9.92689 17.1269C9.78844 17.2654 9.6144 17.3362 9.40479 17.3394C9.19519 17.3426 9.01795 17.2718 8.87309 17.1269C8.7282 16.982 8.65576 16.8064 8.65576 16.6C8.65576 16.3936 8.7282 16.218 8.87309 16.0731L12.9462 12Z"
										fill="white"
									/>
								</g>
							</svg>
						</button>
					</div>
				</div>
			}
		</>
	);
});

export default SolarProductConfirmation;
